import clsx from 'clsx';
import { Icon } from 'common/icon';

import styles from './styles.module.css';

import { ICasino } from 'constants/types';
import { LogoOverlay, LogoOverlayWrapper } from 'components/logo-overlay';
import Image from 'next/image';
import { initialValues, model, validationSchema } from './config';

import { useCallback, useRef } from 'react';
import { useRouter } from 'next/router';
import { useState, useMemo } from 'react';
import { useAlert } from 'components';
import { useCreateCasinoComplaintsMutation } from 'hooks/api/complaints';
import { useMeQuery } from 'hooks/api/users';
import { useCasinosQuery } from 'hooks/api/casino';
import { useClickOutside } from 'hooks/useClickOutside';
import { useComplaintsPagination } from 'hooks/useComplaintsPagination';

import { Dropdown, DropdownRef } from 'components/dropdown/slots';
import { DropdownItem } from 'components/dropdown/slots/item';

import { InputField } from 'common';
import { Button } from 'common/new-button';
import { Formik, FormikValues } from 'formik';
import { handleMutation } from 'utils/handleMutation';

import { SelectArrow } from 'components/modal-arrow';
import { Routes } from 'constants/routes';
import { crocodileSearch } from 'utils/search';
import { shortenFileName } from 'utils/file';

export const CasinoButton = ({
  isOpen,
  onClick,
  disabled,
  placeholder,
  searchQuery,
  selectedCasino,
  setSearchQuery,
}: {
  isOpen?: boolean;
  disabled?: boolean;
  placeholder?: string;
  searchQuery: string;
  onClick?: () => void;
  selectedCasino: ICasino | null;
  setSearchQuery: (query: string) => void;
}) => {
  const alert = useAlert();
  const handleButtonClick = () => {
    if (disabled) {
      alert.error('Please wait for the casinos to load');
    }

    if (onClick) {
      onClick();
    }
  };

  if (isOpen) {
    return (
      <div
        className={clsx(styles.casino_row_button, {
          [styles.disabled]: disabled,
        })}
        onClick={handleButtonClick}
      >
        <input
          autoFocus
          disabled={disabled}
          value={searchQuery}
          placeholder={placeholder ?? 'Casino'}
          className={styles.casino_search_input}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <SelectArrow isOpen={isOpen} />
      </div>
    );
  }

  if (!selectedCasino) {
    return (
      <div
        className={clsx(styles.casino_row_button, {
          [styles.disabled]: disabled,
        })}
        onClick={handleButtonClick}
      >
        <span className={styles.casino_name}>{placeholder ?? 'Casino'}</span>
        <SelectArrow isOpen={isOpen} />
      </div>
    );
  }

  return (
    <div className={styles.casino_row_button} onClick={handleButtonClick}>
      <Image
        src={selectedCasino?.imageUrl}
        width={40}
        height={40}
        alt={selectedCasino?.name}
      />
      <span className={styles.casino_name}>{selectedCasino?.name}</span>

      <SelectArrow isOpen={isOpen} />
    </div>
  );
};

type Props = {
  isVisible: boolean;
  setVisible: (isVisible: boolean) => void;
};

export const ComplaintModal = ({ isVisible, setVisible }: Props) => {
  const alert = useAlert();
  const router = useRouter();
  const modalRef = useRef<HTMLDivElement | null>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { data: { results: casinos = [] } = {}, isFetching } = useCasinosQuery(
    {}
  );
  const { resetPagination } = useComplaintsPagination({ collection: null });
  const data = useMemo(() => {
    if (!searchQuery?.length) return casinos;

    return casinos?.filter((currentCasino) => {
      return crocodileSearch(currentCasino?.name, searchQuery);
    });
  }, [casinos, searchQuery]);
  const dropdownRef = useRef<DropdownRef | null>(null);
  const { data: me } = useMeQuery();
  const [placeComplaint, mutation] = useCreateCasinoComplaintsMutation();
  const isLoading = mutation.isLoading;

  const onSuccess = useCallback(() => {
    alert.success('Complaint successfully added');
  }, [alert]);

  handleMutation({ ...mutation, onSuccess });

  const handleToggle = () => {
    if (isFetching) return;
    setIsOpen(!isOpen);
  };

  const onSubmit = async (values: FormikValues) => {
    const { casino: casinoString, subject, message, screenshot } = values;
    let casino = {} as ICasino;
    if (!subject && !message) {
      alert.error('Please fill fields');
      return;
    }

    if (!me?.user?._id) {
      router.push(Routes.login);
      return;
    }

    if (casinoString) {
      casino = JSON.parse(casinoString);
    } else {
      alert.error('Please select casino');
      return;
    }
    try {
      const response = await placeComplaint({
        casinoId: casino._id,
        message: message,
        title: subject,
        parent: null,
        image: screenshot,
      });
      const complaintId = response?._id;

      if (complaintId) {
        router.push(Routes.complaint(complaintId));
      }

      alert.success('Complaint successfully added');
      resetPagination();
      setVisible(false);
    } catch (error) {
      alert.error('Failed to submit complaint');
    }
  };

  const handleCloseModal = () => {
    setVisible(false);
    setIsOpen(false);
    setSearchQuery('');
    dropdownRef.current?.close();
  };

  useClickOutside({ onClick: handleCloseModal, ref: modalRef });

  if (!isVisible) {
    return null;
  }

  return (
    <div className={clsx(styles.modalBody, styles.modalBodyActive)}>
      <Formik
        onSubmit={onSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({
          dirty,
          values,
          isSubmitting,
          submitForm,
          validateForm,
          setFieldValue,
        }) => (
          <div className={styles.wrapper} ref={modalRef}>
            <div className={styles.top}>
              <h2 className={styles.title}>New complaint</h2>
              <div className={styles.closeButton} onClick={handleCloseModal}>
                <Icon.Close width={20} height={20} />
              </div>
            </div>

            {(isFetching || casinos?.length > 0) && (
              <Dropdown
                variant={data?.length == 0 ? 'NoResults' : undefined}
                ref={dropdownRef}
                button={
                  <CasinoButton
                    disabled={isFetching}
                    isOpen={isOpen}
                    placeholder={
                      isFetching ? 'Searching casinos...' : undefined
                    }
                    onClick={handleToggle}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    selectedCasino={
                      values?.casino
                        ? (JSON.parse(values?.casino) as ICasino)
                        : null
                    }
                  />
                }
              >
                {data?.length > 0 ? (
                  data?.map((casino) => (
                    <DropdownItem
                      key={casino._id}
                      onClick={() => {
                        setFieldValue('casino', JSON.stringify(casino));
                        dropdownRef?.current?.close();
                        handleToggle();
                      }}
                    >
                      <div className={styles.slot_row}>
                        <div className={styles.wrapper_name}>
                          <LogoOverlayWrapper size={60}>
                            <LogoOverlay
                              width={60}
                              height={60}
                              alt={casino?.name}
                              image={casino?.logoUrl}
                            />
                            {casino.imageUrl && (
                              <Image
                                src={casino.imageUrl}
                                width={60}
                                height={60}
                                alt={casino.name}
                              />
                            )}
                          </LogoOverlayWrapper>
                          <span className={styles.casino_name}>
                            {casino.name}
                          </span>
                        </div>
                      </div>
                    </DropdownItem>
                  ))
                ) : (
                  <div className={styles.no_results}>{'No results'}</div>
                )}
              </Dropdown>
            )}
            <div className={styles.multiTextInput}>
              <InputField {...model.subject} />
            </div>
            <div className={styles.textInput}>
              <InputField {...model.message} />
              <div className={styles.actions_background}>
                <div className={styles.actions}>
                  <div className={styles.screenshotInput}>
                    <InputField {...model.screenshot} />
                    <div className={styles.wrapperScreenshotButton}>
                      <Icon.Photo width="20px" />
                      <span>
                        {shortenFileName((values?.screenshot as any)?.name)}
                      </span>
                    </div>
                  </div>

                  <div className={styles.submitButton}>
                    <Button
                      text={isLoading ? 'Loading...' : 'Send'}
                      onClick={() => {
                        validateForm();
                        submitForm();
                      }}
                      disabled={!dirty || isSubmitting}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Formik>
    </div>
  );
};
